/**
 * Description
 * @param {string} color:string - Should be able to accept any color value
 * @param {number} opacity:number - Opacity value between 0 and 100 (if not set, try to find <alpha-value>)
 * @returns {string}
 */
export function colorMix(color: string): string;
export function colorMix(color: string, opacity: number): string;
export function colorMix(color: string, opacity?: number): string {
  if (!opacity) return `color-mix(in srgb, ${color} calc(100% * <alpha-value>), transparent)`;
  return `color-mix(in srgb, ${color} ${opacity}%, transparent)`;
}

export const colorPalettes = {
  inherit: 'inherit',
  current: 'currentColor',
  transparent: 'transparent',
  white: '#ffffff',
  black: '#000000',
  slate: {
    950: '#020617',
    900: '#0f172a',
    800: '#1e293b',
    700: '#334155',
    600: '#475569',
    500: '#64748b',
    400: '#94a3b8',
    300: '#cbd5e1',
    200: '#e2e8f0',
    100: '#f1f5f9',
    50: '#f8fafc',
  },
  gray: {
    950: '#030712',
    900: '#111827',
    800: '#1f2937',
    700: '#374151',
    600: '#4b5563',
    500: '#6b7280',
    400: '#9ca3af',
    300: '#d1d5db',
    200: '#e5e7eb',
    100: '#f3f4f6',
    50: '#f9fafb',
  },
  zinc: {
    950: '#09090b',
    900: '#18181b',
    800: '#27272a',
    700: '#3f3f46',
    600: '#52525b',
    500: '#71717a',
    400: '#a1a1aa',
    300: '#d4d4d8',
    200: '#e4e4e7',
    100: '#f4f4f5',
    50: '#fafafa',
  },
  neutral: {
    950: '#0a0a0a',
    900: '#171717',
    800: '#262626',
    700: '#404040',
    600: '#525252',
    500: '#737373',
    400: '#a3a3a3',
    300: '#d4d4d4',
    200: '#e5e5e5',
    100: '#f5f5f5',
    50: '#fafafa',
  },
  stone: {
    950: '#0c0a09',
    900: '#1c1917',
    800: '#292524',
    700: '#44403c',
    600: '#57534e',
    500: '#78716c',
    400: '#a8a29e',
    300: '#d6d3d1',
    200: '#e7e5e4',
    100: '#f5f5f4',
    50: '#fafaf9',
  },
  red: {
    950: '#450a0a',
    900: '#7f1d1d',
    800: '#991b1b',
    700: '#b91c1c',
    600: '#dc2626',
    500: '#ef4444',
    400: '#f87171',
    300: '#fca5a5',
    200: '#fecaca',
    100: '#fee2e2',
    50: '#fef2f2',
  },
  orange: {
    950: '#431407',
    900: '#7c2d12',
    800: '#9a3412',
    700: '#c2410c',
    600: '#ea580c',
    500: '#f97316',
    400: '#fb923c',
    300: '#fdba74',
    200: '#fed7aa',
    100: '#ffedd5',
    50: '#fff7ed',
  },
  amber: {
    950: '#451a03',
    900: '#78350f',
    800: '#92400e',
    700: '#b45309',
    600: '#d97706',
    500: '#f59e0b',
    400: '#fbbf24',
    300: '#fcd34d',
    200: '#fde68a',
    100: '#fef3c7',
    50: '#fffbeb',
  },
  yellow: {
    950: '#422006',
    900: '#713f12',
    800: '#854d0e',
    700: '#a16207',
    600: '#ca8a04',
    500: '#eab308',
    400: '#facc15',
    300: '#fde047',
    200: '#fef08a',
    100: '#fef9c3',
    50: '#fefce8',
  },
  lime: {
    950: '#1a2e05',
    900: '#365314',
    800: '#3f6212',
    700: '#4d7c0f',
    600: '#65a30d',
    500: '#84cc16',
    400: '#a3e635',
    300: '#bef264',
    200: '#d9f99d',
    100: '#ecfccb',
    50: '#f7fee7',
  },
  green: {
    950: '#052e16',
    900: '#14532d',
    800: '#166534',
    700: '#15803d',
    600: '#16a34a',
    500: '#22c55e',
    400: '#4ade80',
    300: '#86efac',
    200: '#bbf7d0',
    100: '#dcfce7',
    50: '#f0fdf4',
  },
  emerald: {
    950: '#022c22',
    900: '#064e3b',
    800: '#065f46',
    700: '#047857',
    600: '#059669',
    500: '#10b981',
    400: '#34d399',
    300: '#6ee7b7',
    200: '#a7f3d0',
    100: '#d1fae5',
    50: '#ecfdf5',
  },
  teal: {
    950: '#042f2e',
    900: '#134e4a',
    800: '#115e59',
    700: '#0f766e',
    600: '#0d9488',
    500: '#14b8a6',
    400: '#2dd4bf',
    300: '#5eead4',
    200: '#99f6e4',
    100: '#ccfbf1',
    50: '#f0fdfa',
  },
  cyan: {
    950: '#083344',
    900: '#164e63',
    800: '#155e75',
    700: '#0e7490',
    600: '#0891b2',
    500: '#06b6d4',
    400: '#22d3ee',
    300: '#67e8f9',
    200: '#a5f3fc',
    100: '#cffafe',
    50: '#ecfeff',
  },
  sky: {
    950: '#082f49',
    900: '#0c4a6e',
    800: '#075985',
    700: '#0369a1',
    600: '#0284c7',
    500: '#0ea5e9',
    400: '#38bdf8',
    300: '#7dd3fc',
    200: '#bae6fd',
    100: '#e0f2fe',
    50: '#f0f9ff',
  },
  blue: {
    950: '#172554',
    900: '#1e3a8a',
    800: '#1e40af',
    700: '#1d4ed8',
    600: '#2563eb',
    500: '#3b82f6',
    400: '#60a5fa',
    300: '#93c5fd',
    200: '#bfdbfe',
    100: '#dbeafe',
    50: '#eff6ff',
  },
  indigo: {
    950: '#1e1b4b',
    900: '#312e81',
    800: '#3730a3',
    700: '#4338ca',
    600: '#4f46e5',
    500: '#6366f1',
    400: '#818cf8',
    300: '#a5b4fc',
    200: '#c7d2fe',
    100: '#e0e7ff',
    50: '#eef2ff',
  },
  violet: {
    950: '#2e1065',
    900: '#4c1d95',
    800: '#5b21b6',
    700: '#6d28d9',
    600: '#7c3aed',
    500: '#8b5cf6',
    400: '#a78bfa',
    300: '#c4b5fd',
    200: '#ddd6fe',
    100: '#ede9fe',
    50: '#f5f3ff',
  },
  purple: {
    950: '#3b0764',
    900: '#581c87',
    800: '#6b21a8',
    700: '#7e22ce',
    600: '#9333ea',
    500: '#a855f7',
    400: '#c084fc',
    300: '#d8b4fe',
    200: '#e9d5ff',
    100: '#f3e8ff',
    50: '#faf5ff',
  },
  fuchsia: {
    950: '#4a044e',
    900: '#701a75',
    800: '#86198f',
    700: '#a21caf',
    600: '#c026d3',
    500: '#d946ef',
    400: '#e879f9',
    300: '#f0abfc',
    200: '#f5d0fe',
    100: '#fae8ff',
    50: '#fdf4ff',
  },
  pink: {
    950: '#500724',
    900: '#831843',
    800: '#9d174d',
    700: '#be185d',
    600: '#db2777',
    500: '#ec4899',
    400: '#f472b6',
    300: '#f9a8d4',
    200: '#fbcfe8',
    100: '#fce7f3',
    50: '#fdf2f8',
  },
  rose: {
    950: '#4c0519',
    900: '#881337',
    800: '#9f1239',
    700: '#be123c',
    600: '#e11d48',
    500: '#f43f5e',
    400: '#fb7185',
    300: '#fda4af',
    200: '#fecdd3',
    100: '#ffe4e6',
    50: '#fff1f2',
  },
  surface: {
    0: colorMix('var(--p-surface-0)'),
    50: colorMix('var(--p-surface-50)'),
    100: colorMix('var(--p-surface-100)'),
    200: colorMix('var(--p-surface-200)'),
    300: colorMix('var(--p-surface-300)'),
    400: colorMix('var(--p-surface-400)'),
    500: colorMix('var(--p-surface-500)'),
    600: colorMix('var(--p-surface-600)'),
    700: colorMix('var(--p-surface-700)'),
    800: colorMix('var(--p-surface-800)'),
    900: colorMix('var(--p-surface-900)'),
    950: colorMix('var(--p-surface-950)'),
  },

  'p-success-color': 'var(--p-success-color)',
  'p-color': 'var(--p-text-color)',
  'p-color-emphasis': 'var(--p-text-hover-color)',
  'p-muted-color': 'var(--p-text-muted-color)',
  'p-muted-color-emphasis': 'var(--p-text-hover-muted-color)',
  'p-content-background': 'var(--p-content-background)',
  'p-content-border': 'var(--p-content-border-color)',
  'p-color-primary': 'var(--p-primary-color)',
  'p-background-focus-color': 'var(--p-navigation-item-focus-background)',

  success: 'var(--p-green-500)',
  warning: 'var(--p-yellow-500)',
  danger: 'var(--p-red-500)',
  info: 'var(--p-primary-color)',
  pending: 'var(--p-yellow-500)',
};
